export {
  isDraggable,
  isDropzone,
  sanitizeQuestionData,
  getInitPropsForNewShape,
  stableOrderizeData,
  getImageSizeWithAspectRatio,
  convertToPercentage,
  convertToPixel,
  findHighestMidPointInPolygon,
  getDragAndDropCanvasWidth,
  injectCorrectElementsInData,
  extractCorrectElementsFromData,
} from "../../DragAndDropField/DragAndDrop/DragAndDropUtils";
import { MODE_TEACHER_CREATE } from "Worksheet/modules/WorksheetConstants";
import { stableOrderizeData } from "./../../DragAndDropField/DragAndDrop/DragAndDropUtils";
import { LABELLING_CATEGORY } from "./LabellingConstant";
import _ from "lodash";

/**
 *
 * @param {*} data
 * @returns convert pixel to percentage
 */
export const convertDataToPercentage = (elements, fieldId, mode) => {
  const canvas = document.getElementById(
    `konva-canvas-container-id-${fieldId}-${mode}`
  );
  if (!canvas) return null;

  const canvasHeight = canvas.clientHeight;
  const canvasWidth = canvas.clientWidth;

  // * Property converter
  const convert = (item, property, dimension) => {
    if (!_.has(item, property)) return;
    const convertedValue = (_.get(item, property) / dimension) * 100;
    _.set(item, property, convertedValue);
    return convertedValue;
  };

  const elementsInPercentage = _.map(_.cloneDeep(elements), item => {
    const element = _.cloneDeep(item);
    convert(element, "x", canvasWidth);
    convert(element, "width", canvasWidth);
    convert(element, "radiusX", canvasWidth);

    convert(element, "y", canvasHeight);
    convert(element, "height", canvasHeight);
    convert(element, "radiusY", canvasHeight);
    convert(element, "fontSize", canvasHeight);

    if (_.has(element, "points")) {
      element.points = _.map(element.points, (point, index) => {
        return (point / (index % 2 === 0 ? canvasWidth : canvasHeight)) * 100;
      });
    }

    if (_.has(element, "arrows")) {
      element.arrows = _.map(element.arrows, arrow => {
        if (_.isArray(arrow?.to)) {
          convert(arrow?.to, 0, canvasWidth);
          convert(arrow?.to, 1, canvasHeight);
        }
        return arrow;
      });
    }

    return element;
  });

  return elementsInPercentage;
};

/**
 *
 * @param {*} data
 * @returns convert percentage to pixel
 */
export const convertDataToPixel = async (
  fieldValue,
  fieldId,
  mode,
  onConvert = () => {},
  t = () => {},
  viewSolution = false,
  worksheetMode
) => {
  const placeholder = getLabelPlaceholder(t, mode);
  const canvas = document.getElementById(
    `konva-canvas-container-id-${fieldId}-${mode}`
  );
  if (!canvas) {
    // * retry till canvas is not added into dom
    await new Promise(resolve => setTimeout(() => resolve(), 100));
    return convertDataToPixel(fieldValue, fieldId, mode, onConvert, t);
  }

  const elements = _.get(fieldValue, "elements", []);
  const labellingAnswer = _.get(
    fieldValue,
    mode === "create" || worksheetMode === MODE_TEACHER_CREATE || viewSolution
      ? "labellingAnswer"
      : "answers",
    {}
  );

  const canvasHeight = canvas.clientHeight;
  const canvasWidth = canvas.clientWidth;

  // * Property converter
  const convert = (item, property, dimension) => {
    if (!_.has(item, property)) return;
    _.set(
      item,
      property,
      (parseFloat(_.get(item, property)) / 100) * dimension
    );
  };

  // * Converting elements
  const updatedData = _.map(elements, item => {
    const element = _.cloneDeep(item);

    convert(element, "x", canvasWidth);
    convert(element, "width", canvasWidth);
    convert(element, "radiusX", canvasWidth);

    convert(element, "y", canvasHeight);
    convert(element, "height", canvasHeight);
    convert(element, "radiusY", canvasHeight);
    convert(element, "fontSize", canvasHeight);

    if (_.has(element, "points")) {
      element.points = _.map(element.points, (point, index) => {
        return (point / 100) * (index % 2 === 0 ? canvasWidth : canvasHeight);
      });
    }

    if (_.has(element, "arrows")) {
      element.arrows = _.map(element.arrows, arrow => {
        if (_.isArray(arrow?.to)) {
          convert(arrow?.to, 0, canvasWidth);
          convert(arrow?.to, 1, canvasHeight);
        }
        return arrow;
      });
    }

    /**
     * For create mode, sending answer text with ; join
     * For edit mode, sending string as it is
     * For view solution, using create mode flow, without edit
     */
    if (_.get(element, "category") === LABELLING_CATEGORY.LABEL) {
      const id = _.get(element, "id");
      let text = _.get(element, "text", "");
      if (mode === "create" || worksheetMode === MODE_TEACHER_CREATE) {
        text = _.join(_.get(labellingAnswer, id, ""), ";").trim();
      } else {
        text = _.get(labellingAnswer, id, "");
      }
      _.set(element, "isPlaceholder", _.isEmpty(text));
      _.set(element, "text", text || placeholder);

      if (mode === "view") {
        // * Checking is answer is correct or not, using FILL IN THE BLANK logic
        const correctAnswers = _.get(fieldValue, ["labellingAnswer", id]);
        const isCorrect = _.includes(
          _.map(correctAnswers, _ans => _.toLower(_.trim(_ans))),
          _.toLower(_.trim(text))
        );
        _.set(element, "isCorrect", isCorrect);
      }
    }

    return element;
  });

  onConvert(updatedData);
};

/**
 *
 * @param {*} data
 * @param {*} mode
 * @returns mapping draggable state, according to user and mode
 */
export const sanitizeLabellingQuestionData = (data, { mode, drawMode }) => {
  const mappedData = _.map(data, item => {
    const itemObject = { ...item };

    if (mode === "create" && !_.get(drawMode, "isDrawing")) {
      itemObject.draggable = true;
    } else {
      itemObject.draggable = false;
    }
    return itemObject;
  });

  return stableOrderizeData(mappedData);
};

/**
 *
 * @param {*} t
 * @param {*} mode
 * @returns Label fill text input placeholder
 */
export const getLabelPlaceholder = (t, mode) => {
  return t(mode === "create" ? "common:add_with_label" : "common:typeHere", {
    label: t("worksheet:answer"),
  });
};
